import grants from "./reviewSurfaces.generated.json";

/**
 * Which permission action opens which surface of the review page.
 *
 * The table itself is generated from `permissions/review_surfaces.py` and
 * imported here rather than restated, so the frontend and the backend cannot
 * answer the question differently — there is only one answer, and `make
 * docs-check` fails if the checked-in copy has gone stale.
 *
 * Scope is the page's structure: opening it, each tab, and each tab's download
 * or CRUD control. The write verbs inside the Review tab keep their own gates.
 */
export type TReviewSurface = keyof typeof grants;

/** Every surface, for exhaustiveness checks and tests. */
export const REVIEW_SURFACES = Object.keys(grants) as TReviewSurface[];

export type THasSurfaceOptions = {
    /**
     * Whether the anchor file is a FinJournal. Only the review-grant surfaces
     * differ by anchor type; the rest answer the same either way, so call sites
     * that are not about a file may omit it.
     */
    isFinJournal?: boolean;
};

/**
 * Whether a user holding `permissions` may see `surface`.
 *
 * Any-of: holding any single action listed for the surface is enough. This is a
 * display gate only — the endpoints behind each surface enforce the same actions
 * themselves, and are what actually protect the data.
 */
export function hasSurface(
    surface: TReviewSurface,
    permissions: readonly string[],
    options?: THasSurfaceOptions,
): boolean {
    const actions: readonly string[] =
        grants[surface][options?.isFinJournal === true ? "finjournal" : "tbgl"];
    return actions.some((action) => permissions.includes(action));
}
